import '../operations.css';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlignCenter, AlignLeft, AlignRight, Bold, Edit, Heading1, Heading2,
  ImagePlus, Italic, Link, List, ListOrdered, Plus, Redo2,
  RemoveFormatting, Trash2, Underline, Undo2,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { MakeWithAIDialog } from '../../components/ai/MakeWithAIDialog';
import { applyDocumentSelection, captureDocumentSelection, captureTextSelection, replaceText, type AssistSnapshot } from '../../components/ai/contentAssist';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Table } from '../../components/ui/Table';
import { Modal } from '../../components/ui/Modal';
import { templatesApi } from '../../api/templates';
import type { Template } from '../../types';

const SEARCH_DEBOUNCE_MS = 300;
const emailPreviewPolicy = `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; script-src 'none'; style-src 'unsafe-inline' https: http:; img-src data: blob: https: http:; font-src data: https: http:; form-action 'none'; base-uri 'none';">`;
const logoWidths = { small: 72, medium: 140, large: 220 } as const;
type LogoSize = keyof typeof logoWidths;
type LogoAlign = 'left' | 'center' | 'right';

const difficultyStyles: Record<string, string> = {
  easy: 'bg-green-100 text-green-800',
  medium: 'bg-yellow-100 text-yellow-800',
  hard: 'bg-red-100 text-red-800',
};

const baseBadgeClass = 'inline-block px-2 py-0.5 rounded-full text-xs font-medium';

function MetadataBadge({ value, className }: { value: string; className: string }) {
  return <span className={`${baseBadgeClass} ${className}`}>{value}</span>;
}

function htmlFromVisualDocument(doc: Document, source: string): string {
  const clone = doc.documentElement.cloneNode(true) as HTMLElement;
  clone.querySelectorAll('meta[http-equiv="Content-Security-Policy" i]').forEach((node) => node.remove());
  const type = /<!doctype html/i.test(source) ? '<!DOCTYPE html>' : '';
  return `${type}${clone.outerHTML}`;
}

function insertLogo(doc: Document, src: string, align: LogoAlign, width: number) {
  const block = doc.createElement('p');
  block.style.textAlign = align;
  block.style.margin = '0 0 16px';
  const image = doc.createElement('img');
  image.src = src;
  image.alt = 'Logo';
  image.width = width;
  image.style.cssText = `width:${width}px;height:auto;max-width:100%;display:inline-block`;
  block.appendChild(image);
  doc.body.insertBefore(block, doc.body.firstChild);
}

function openEmailPreview(html: string): boolean {
  const popup = window.open('', 'awarenow-email-preview', 'popup,width=1100,height=800');
  if (!popup) return false;
  popup.document.open();
  popup.document.write('<!DOCTYPE html><html><head><meta charset="utf-8"><title>Email template preview</title><style>html,body{margin:0;height:100%;background:#fff}iframe{border:0;width:100%;height:100%;}</style></head><body></body></html>');
  popup.document.close();
  const frame = popup.document.createElement('iframe');
  frame.title = 'Email template preview';
  frame.setAttribute('sandbox', '');
  frame.referrerPolicy = 'no-referrer';
  frame.srcdoc = `${emailPreviewPolicy}${html}`;
  popup.document.body.replaceChildren(frame);
  popup.focus();
  return true;
}

export function TemplateList() {
  const queryClient = useQueryClient();
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; template: Template | null }>({
    open: false,
    template: null,
  });
  const [editor, setEditor] = useState<{ open: boolean; template: Template | null }>({
    open: false,
    template: null,
  });
  const [previewHtml, setPreviewHtml] = useState('');
  const [visual, setVisual] = useState(false);
  const [visualHtml, setVisualHtml] = useState('');
  const [logoOpen, setLogoOpen] = useState(false);
  const [logoSrc, setLogoSrc] = useState('');
  const [logoSize, setLogoSize] = useState<LogoSize>('medium');
  const [logoAlign, setLogoAlign] = useState<LogoAlign>('center');
  const [editorError, setEditorError] = useState<string | null>(null);
  const [assist, setAssist] = useState<AssistSnapshot | null>(null);
  const visualFrame = useRef<HTMLIFrameElement>(null);
  const subjectRef = useRef<HTMLInputElement>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const htmlRef = useRef<HTMLTextAreaElement>(null);
  const activeAssistField = useRef<'subject' | 'html' | 'text'>('html');

  const [category, setCategory] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [vector, setVector] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  // Debounce the free-text search so we don't refetch on every keystroke.
  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const { data: facets } = useQuery({
    queryKey: ['templateFacets'],
    queryFn: async () => {
      const res = await templatesApi.getFacets();
      return res.data;
    },
  });

  const { data: templates, isLoading, isFetching, isError } = useQuery({
    queryKey: ['templates', 'library', { category, difficulty, vector, search }],
    queryFn: async () => {
      const res = await templatesApi.getLibrary({
        category: category || undefined,
        difficulty: difficulty || undefined,
        vector: vector || undefined,
        q: search || undefined,
      });
      return res.data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => templatesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      queryClient.invalidateQueries({ queryKey: ['templateFacets'] });
      setDeleteModal({ open: false, template: null });
    },
  });

  const saveMutation = useMutation({
    mutationFn: ({
      id,
      name,
      subject,
      html,
      text,
      envelope_sender,
      category,
      difficulty,
      impersonated_brand,
      attack_vector,
    }: {
      id: number | null;
      name: string;
      subject: string;
      html: string;
      text: string;
      envelope_sender: string;
      category: string;
      difficulty: string;
      impersonated_brand: string;
      attack_vector: string;
    }) =>
      id === null
        ? templatesApi.create({ name, subject, html, text, envelope_sender, category, difficulty, impersonated_brand, attack_vector })
        : templatesApi.update(id, { name, subject, html, text, envelope_sender, category, difficulty, impersonated_brand, attack_vector }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      queryClient.invalidateQueries({ queryKey: ['templateFacets'] });
      setEditor({ open: false, template: null });
    },
  });

  const readVisualHtml = () => {
    const doc = visualFrame.current?.contentDocument;
    if (!doc?.documentElement) return previewHtml;
    return htmlFromVisualDocument(doc, previewHtml);
  };

  const toggleVisual = () => {
    if (visual) {
      setPreviewHtml(readVisualHtml());
      setVisual(false);
      return;
    }
    setVisualHtml(`${emailPreviewPolicy}${previewHtml}`);
    activeAssistField.current = 'html';
    setVisual(true);
  };

  const enableVisualEditing = () => {
    const doc = visualFrame.current?.contentDocument;
    if (!doc?.body) return;
    doc.body.contentEditable = 'true';
    doc.body.addEventListener('input', () => setPreviewHtml(readVisualHtml()));
  };

  const formatVisual = (command: string, value?: string) => {
    const doc = visualFrame.current?.contentDocument;
    if (!doc) return;
    visualFrame.current?.contentWindow?.focus();
    doc.execCommand(command, false, value);
    setPreviewHtml(readVisualHtml());
  };

  const chooseLogo = (file: File | undefined) => {
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/gif', 'image/webp'].includes(file.type)) {
      setEditorError('Use a PNG, JPEG, GIF, or WebP logo.');
      return;
    }
    if (file.size > 400 * 1024) {
      setEditorError('Logo must be 400 KB or smaller.');
      return;
    }
    setEditorError(null);
    const reader = new FileReader();
    reader.onload = () => setLogoSrc(typeof reader.result === 'string' ? reader.result : '');
    reader.readAsDataURL(file);
  };

  const placeLogo = () => {
    const doc = visualFrame.current?.contentDocument;
    if (!doc?.body || !logoSrc) return;
    insertLogo(doc, logoSrc, logoAlign, logoWidths[logoSize]);
    setPreviewHtml(readVisualHtml());
  };

  const handlePreview = () => {
    setEditorError(null);
    if (!openEmailPreview(visual ? readVisualHtml() : previewHtml)) {
      setEditorError('The preview window was blocked. Allow pop-ups for this site and try again.');
    }
  };

  const captureEmailAssist = () => {
    const html = visual ? readVisualHtml() : (htmlRef.current?.value ?? previewHtml);
    const text = textRef.current?.value ?? '';
    const subject = subjectRef.current?.value ?? '';
    const form = subjectRef.current?.form;
    const data = form ? new FormData(form) : null;
    const metadata = {
      name: String(data?.get('name') ?? ''),
      subject,
      category: String(data?.get('category') ?? ''),
      difficulty: String(data?.get('difficulty') ?? ''),
      attack_vector: String(data?.get('attack_vector') ?? ''),
      impersonated_brand: String(data?.get('impersonated_brand') ?? ''),
    };
    if (activeAssistField.current === 'html' && visual) {
      const selected = visualFrame.current?.contentDocument ? captureDocumentSelection(visualFrame.current.contentDocument) : null;
      setAssist({
        target: 'email_html',
        label: 'Email HTML',
        scope: selected ? 'selection' : 'field',
        selectedText: selected?.selectedText ?? '',
        beforeText: '',
        afterText: '',
        documentHtml: html,
        documentText: text,
        fieldValue: html,
        selectionStart: null,
        selectionEnd: null,
        range: selected?.range ?? null,
        metadata,
      });
      return;
    }
    const field = activeAssistField.current;
    const element = field === 'subject' ? subjectRef.current : field === 'text' ? textRef.current : htmlRef.current;
    const value = element?.value ?? (field === 'subject' ? subject : field === 'text' ? text : html);
    const captured = captureTextSelection(value, element?.selectionStart ?? null, element?.selectionEnd ?? null);
    if (captured.selectedText.length > 20000) {
      setEditorError('Select 20,000 characters or fewer.');
      return;
    }
    if (captured.scope === 'field' && value.length > 100000) {
      setEditorError('This field is too long for AI assistance.');
      return;
    }
    setEditorError(null);
    setAssist({
      target: field === 'subject' ? 'email_subject' : field === 'text' ? 'email_text' : 'email_html',
      label: field === 'subject' ? 'Email subject' : field === 'text' ? 'Email plain text' : 'Email HTML',
      ...captured,
      documentHtml: html,
      documentText: field === 'subject' ? subject : text,
      fieldValue: value,
      range: null,
      metadata,
    });
  };

  const applyEmailAssist = (replacement: string) => {
    if (!assist) return;
    if (assist.range && visualFrame.current?.contentDocument) {
      applyDocumentSelection(visualFrame.current.contentDocument, assist.range, replacement);
      setPreviewHtml(readVisualHtml());
    } else if (assist.target === 'email_html') {
      const next = replaceText(assist.fieldValue, assist.selectionStart, assist.selectionEnd, replacement);
      setPreviewHtml(next);
      if (visual) setVisualHtml(`${emailPreviewPolicy}${next}`);
    } else if (assist.target === 'email_subject' && subjectRef.current) {
      subjectRef.current.value = replaceText(assist.fieldValue, assist.selectionStart, assist.selectionEnd, replacement);
    } else if (assist.target === 'email_text' && textRef.current) {
      textRef.current.value = replaceText(assist.fieldValue, assist.selectionStart, assist.selectionEnd, replacement);
    }
    setAssist(null);
  };

  const openEditor = (template: Template | null) => {
    setPreviewHtml(template?.html ?? '');
    setVisual(false);
    setLogoOpen(false);
    setLogoSrc('');
    setEditorError(null);
    setEditor({ open: true, template });
  };

  const columns = [
    {
      key: 'name',
      header: 'Name',
      render: (template: Template) => (
        <div>
          <p className="font-medium">{template.name}</p>
          <p className="text-sm text-gray-500">{template.subject}</p>
        </div>
      ),
    },
    {
      key: 'metadata',
      header: 'Metadata',
      render: (template: Template) => {
        const hasMetadata =
          template.category || template.difficulty || template.attack_vector || template.impersonated_brand;
        if (!hasMetadata) {
          return <span className="text-sm text-gray-400">Uncategorized</span>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            {template.impersonated_brand && (
              <MetadataBadge value={template.impersonated_brand} className="bg-gray-100 text-gray-700" />
            )}
            {template.category && (
              <MetadataBadge value={template.category} className="bg-indigo-100 text-indigo-800" />
            )}
            {template.attack_vector && (
              <MetadataBadge value={template.attack_vector} className="bg-blue-100 text-blue-800" />
            )}
            {template.difficulty && (
              <MetadataBadge
                value={template.difficulty}
                className={difficultyStyles[template.difficulty.toLowerCase()] ?? 'bg-gray-100 text-gray-700'}
              />
            )}
          </div>
        );
      },
    },
    {
      key: 'modified_date',
      header: 'Last Modified',
      render: (template: Template) => (
        <span className="text-sm text-gray-500">
          {new Date(template.modified_date).toLocaleDateString()}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (template: Template) => (
        <div className="flex items-center gap-2 justify-end">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openEditor(template);
            }}
            className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
            title="Edit"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setDeleteModal({ open: true, template });
            }}
            className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  const selectClass =
    'w-full min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500';

  return (
    <div className="operations-page space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="operations-eyebrow">Content library</p>
          <h1 className="text-2xl font-bold text-gray-900">Email Templates</h1>
          <p className="text-gray-500 mt-1">Browse and manage your phishing template library</p>
        </div>
        <Button onClick={() => openEditor(null)}>
         <Plus className="w-4 h-4 mr-2" />
         New Template
       </Button>
      </div>

      <Card>
        <div className="flex flex-wrap items-end gap-4 mb-4">
          <div>
            <label htmlFor="template-category-filter" className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              id="template-category-filter"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className={selectClass}
            >
              <option value="">All</option>
              {(facets?.categories ?? []).map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="template-difficulty-filter" className="block text-sm font-medium text-gray-700 mb-1">
              Difficulty
            </label>
            <select
              id="template-difficulty-filter"
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value)}
              className={selectClass}
            >
              <option value="">All</option>
              {(facets?.difficulties ?? []).map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="template-vector-filter" className="block text-sm font-medium text-gray-700 mb-1">
              Attack vector
            </label>
            <select
              id="template-vector-filter"
              value={vector}
              onChange={(e) => setVector(e.target.value)}
              className={selectClass}
            >
              <option value="">All</option>
              {(facets?.vectors ?? []).map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label htmlFor="template-search" className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <input
              id="template-search"
              type="text"
              placeholder="Name, subject, or brand"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className={selectClass}
            />
          </div>
          {isFetching && !isLoading && (
            <span className="text-sm text-gray-400">Updating...</span>
          )}
        </div>

        {isError ? (
          <p role="alert" className="text-red-700">Could not load templates. Refresh the page to try again.</p>
        ) : isLoading ? (
          <div className="animate-pulse space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-100 rounded"></div>
            ))}
          </div>
        ) : (
          <Table
            columns={columns}
            data={templates ?? []}
            keyExtractor={(t) => t.id}
            emptyMessage="No templates match these filters. Try widening your search."
          />
        )}
      </Card>

      <Modal
        isOpen={editor.open}
        onClose={() => {
          if (!saveMutation.isPending) setEditor({ open: false, template: null });
        }}
        title={editor.template ? 'Edit Template' : 'New Template'}
        size="xl"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (saveMutation.isPending) return;
            const data = new FormData(e.currentTarget);
            saveMutation.mutate({
              id: editor.template?.id ?? null,
              name: String(data.get('name') ?? ''),
              subject: String(data.get('subject') ?? ''),
              envelope_sender: String(data.get('envelope_sender') ?? ''),
              html: visual ? readVisualHtml() : String(data.get('html') ?? ''),
              text: String(data.get('text') ?? ''),
              category: String(data.get('category') ?? '').trim(),
              difficulty: String(data.get('difficulty') ?? '').trim(),
              impersonated_brand: String(data.get('impersonated_brand') ?? '').trim(),
              attack_vector: String(data.get('attack_vector') ?? '').trim(),
            });
          }}
          className="space-y-4"
        >
          <div>
            <label htmlFor="template-editor-name" className="block text-sm font-medium text-gray-700 mb-1">
              Template name
            </label>
            <input
              id="template-editor-name"
              name="name"
              type="text"
              required
              defaultValue={editor.template?.name ?? ''}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label htmlFor="template-editor-subject" className="block text-sm font-medium text-gray-700 mb-1">
              Subject
            </label>
            <input
              id="template-editor-subject"
              name="subject"
              type="text"
              required
              ref={subjectRef}
              onFocus={() => { activeAssistField.current = 'subject'; }}
              defaultValue={editor.template?.subject ?? ''}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label htmlFor="template-editor-sender" className="block text-sm font-medium text-gray-700 mb-1">
              Sender address
            </label>
            <input
              id="template-editor-sender"
              name="envelope_sender"
              type="text"
              placeholder="Security Team &lt;security@example.com&gt;"
              defaultValue={editor.template?.envelope_sender ?? ''}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <fieldset className="grid gap-4 rounded-lg border border-gray-200 p-4 sm:grid-cols-2">
            <legend className="px-1 text-sm font-semibold text-gray-700">Template classification</legend>
            <div>
              <label htmlFor="template-editor-category" className="block text-sm font-medium text-gray-700 mb-1">
                Template category
              </label>
              <input
                id="template-editor-category"
                name="category"
                list="template-category-options"
                defaultValue={editor.template?.category ?? ''}
                placeholder="Choose or type a new category"
                className={selectClass}
              />
              <datalist id="template-category-options">
                {(facets?.categories ?? []).map((item) => <option key={item} value={item} />)}
              </datalist>
              <p className="mt-1 text-xs text-gray-500">Typing a new value creates that category when saved.</p>
            </div>
            <div>
              <label htmlFor="template-editor-difficulty" className="block text-sm font-medium text-gray-700 mb-1">
                Template difficulty
              </label>
              <select
                id="template-editor-difficulty"
                name="difficulty"
                defaultValue={editor.template?.difficulty?.toLowerCase() ?? ''}
                className={selectClass}
              >
                <option value="">Not set</option>
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>
            <div>
              <label htmlFor="template-editor-vector" className="block text-sm font-medium text-gray-700 mb-1">
                Template attack vector
              </label>
              <input
                id="template-editor-vector"
                name="attack_vector"
                list="template-vector-options"
                defaultValue={editor.template?.attack_vector ?? ''}
                placeholder="Link, QR, attachment, OAuth…"
                className={selectClass}
              />
              <datalist id="template-vector-options">
                {(facets?.vectors ?? []).map((item) => <option key={item} value={item} />)}
              </datalist>
            </div>
            <div>
              <label htmlFor="template-editor-brand" className="block text-sm font-medium text-gray-700 mb-1">
                Impersonated brand
              </label>
              <input
                id="template-editor-brand"
                name="impersonated_brand"
                defaultValue={editor.template?.impersonated_brand ?? ''}
                placeholder="Microsoft, Workday, internal IT…"
                className={selectClass}
              />
            </div>
          </fieldset>
          <div>
            <div className="mb-2 flex items-center justify-between gap-3">
              <label htmlFor="template-editor-html" className="block text-sm font-medium text-gray-700">
                HTML content
              </label>
              <div className="flex items-center gap-2">
                <Button type="button" variant="secondary" size="sm" disabled={saveMutation.isPending} onMouseDown={(event) => { event.preventDefault(); captureEmailAssist(); }}>
                  Make with AI
                </Button>
                <Button type="button" variant="secondary" size="sm" disabled={saveMutation.isPending} onClick={handlePreview}>
                  Preview
                </Button>
                <Button type="button" variant="secondary" size="sm" disabled={saveMutation.isPending} onClick={toggleVisual}>
                  {visual ? 'HTML source' : 'WYSIWYG'}
                </Button>
              </div>
            </div>
            {visual ? (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="secondary" size="sm" aria-label="Bold" onClick={() => formatVisual('bold')}><Bold className="h-4 w-4" /></Button>
                  <Button type="button" variant="secondary" size="sm" aria-label="Italic" onClick={() => formatVisual('italic')}><Italic className="h-4 w-4" /></Button>
                  <Button type="button" variant="secondary" size="sm" aria-label="Underline" onClick={() => formatVisual('underline')}><Underline className="h-4 w-4" /></Button>
                  <Button type="button" variant="secondary" size="sm" aria-label="Heading 1" onClick={() => formatVisual('formatBlock', '<h1>')}><Heading1 className="h-4 w-4" /></Button>
                  <Button type="button" variant="secondary" size="sm" aria-label="Heading 2" onClick={() => formatVisual('formatBlock', '<h2>')}><Heading2 className="h-4 w-4" /></Button>
                  <Button type="button" variant="secondary" size="sm" aria-label="Align left" onClick={() => formatVisual('justifyLeft')}><AlignLeft className="h-4 w-4" /></Button>
                  <Button type="button" variant="secondary" size="sm" aria-label="Align center" onClick={() => formatVisual('justifyCenter')}><AlignCenter className="h-4 w-4" /></Button>
                  <Button type="button" variant="secondary" size="sm" aria-label="Align right" onClick={() => formatVisual('justifyRight')}><AlignRight className="h-4 w-4" /></Button>
                  <Button type="button" variant="secondary" size="sm" aria-label="Bulleted list" onClick={() => formatVisual('insertUnorderedList')}><List className="h-4 w-4" /></Button>
                  <Button type="button" variant="secondary" size="sm" aria-label="Numbered list" onClick={() => formatVisual('insertOrderedList')}><ListOrdered className="h-4 w-4" /></Button>
                  <Button type="button" variant="secondary" size="sm" aria-label="Link" onClick={() => { const url = window.prompt('Link URL'); if (url) formatVisual('createLink', url); }}><Link className="h-4 w-4" /></Button>
                  <Button type="button" variant="secondary" size="sm" aria-label="Clear formatting" onClick={() => formatVisual('removeFormat')}><RemoveFormatting className="h-4 w-4" /></Button>
                  <Button type="button" variant="secondary" size="sm" aria-label="Undo" onClick={() => formatVisual('undo')}><Undo2 className="h-4 w-4" /></Button>
                  <Button type="button" variant="secondary" size="sm" aria-label="Redo" onClick={() => formatVisual('redo')}><Redo2 className="h-4 w-4" /></Button>
                  <Button type="button" variant="secondary" size="sm" aria-pressed={logoOpen} onClick={() => setLogoOpen((open) => !open)}><ImagePlus className="h-4 w-4" /> Logo</Button>
                </div>
                {logoOpen && (
                  <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 p-3">
                    <label className="text-sm">
                      Upload logo
                      <input className="mt-1 block text-sm" type="file" accept="image/png,image/jpeg,image/gif,image/webp" onChange={(event) => chooseLogo(event.target.files?.[0])} />
                    </label>
                    <label className="text-sm">
                      Size
                      <select className="input mt-1" aria-label="Logo size" value={logoSize} onChange={(event) => setLogoSize(event.target.value as LogoSize)}>
                        <option value="small">Small</option>
                        <option value="medium">Medium</option>
                        <option value="large">Large</option>
                      </select>
                    </label>
                    <div className="flex gap-2">
                      <Button type="button" variant={logoAlign === 'left' ? 'primary' : 'secondary'} size="sm" onClick={() => setLogoAlign('left')}>Left</Button>
                      <Button type="button" variant={logoAlign === 'center' ? 'primary' : 'secondary'} size="sm" onClick={() => setLogoAlign('center')}>Center</Button>
                      <Button type="button" variant={logoAlign === 'right' ? 'primary' : 'secondary'} size="sm" onClick={() => setLogoAlign('right')}>Right</Button>
                    </div>
                    <Button type="button" size="sm" disabled={!logoSrc} onClick={placeLogo}>Insert logo</Button>
                    {logoSrc && <img src={logoSrc} alt="Selected logo" className="h-10 w-auto rounded border border-gray-200 bg-white p-1" />}
                  </div>
                )}
                <iframe
                  ref={visualFrame}
                  title="Email template visual editor"
                  sandbox="allow-same-origin"
                  srcDoc={visualHtml}
                  onLoad={enableVisualEditing}
                  className="w-full min-h-80 rounded-lg border border-gray-200 bg-white"
                />
              </div>
            ) : (
              <textarea
                id="template-editor-html"
                name="html"
                required
                ref={htmlRef}
                value={previewHtml}
                onFocus={() => { activeAssistField.current = 'html'; }}
                onChange={(event) => setPreviewHtml(event.target.value)}
                className="w-full min-h-40 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            )}
          </div>
          <div>
            <label htmlFor="template-editor-text" className="block text-sm font-medium text-gray-700 mb-1">
              Plain text
            </label>
            <textarea
              id="template-editor-text"
              name="text"
              required
              ref={textRef}
              onFocus={() => { activeAssistField.current = 'text'; }}
              defaultValue={editor.template?.text ?? ''}
              className="w-full min-h-24 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          {!visual && <div>
            <p className="block text-sm font-medium text-gray-700 mb-1">Preview</p>
            <iframe
              title="Email template preview"
              sandbox=""
              srcDoc={`<meta http-equiv="Content-Security-Policy" content="default-src 'none'; script-src 'none'; style-src 'unsafe-inline'; img-src data: https: http:;">${previewHtml}`}
              className="w-full min-h-40 rounded-lg border border-gray-200 bg-white"
            />
          </div>}
          {editorError && <p role="alert" className="text-sm text-red-700">{editorError}</p>}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="secondary"
              disabled={saveMutation.isPending}
              onClick={() => setEditor({ open: false, template: null })}
            >
              Cancel
            </Button>
            <Button type="submit" loading={saveMutation.isPending}>
              Save template
            </Button>
          </div>
        </form>
      </Modal>
      <MakeWithAIDialog open={assist !== null} snapshot={assist} onApply={applyEmailAssist} onClose={() => setAssist(null)} />

      <Modal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, template: null })}
        title="Delete Template"
        size="sm"
      >
        <p className="text-gray-600 mb-6">
          Are you sure you want to delete "{deleteModal.template?.name}"?
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={() => setDeleteModal({ open: false, template: null })}>
            Cancel
          </Button>
          <Button
            variant="danger"
            loading={deleteMutation.isPending}
            onClick={() => deleteModal.template && deleteMutation.mutate(deleteModal.template.id)}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
}
